<template>
  <div
    class="page"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <div class="card">
      <div class="page-head">
        <h1>Заявки</h1>
        <p>Всего: {{ total }}</p>
      </div>

      <div v-if="pullDistance > 0" class="pull-refresh">
        <div class="pull-refresh__inner">
          {{ isRefreshing ? 'Обновляем...' : pullDistance > 90 ? 'Отпусти, чтобы обновить' : 'Потяни вниз, чтобы обновить' }}
        </div>
      </div>

      <p v-if="errorMessage" class="message error">{{ errorMessage }}</p>

      <div v-if="items.length" class="requests-list">
        <div
          v-for="item in items"
          :key="item.order_number"
          class="request-item"
          @click="openOrder(item.order_number)"
        >
          <div class="request-top">
            <div>
              <div class="order-number">{{ formatOrderNumber(item.order_number) }}</div>
              <strong class="request-title">{{ item.product_name }}</strong>
            </div>
            <span :class="['status', statusClass(item.status)]">{{ item.status }}</span>
          </div>

          <div v-if="isAdmin" class="request-row">
            <strong>Отправитель:</strong>
            <span class="request-text-ellipsis">{{ item.user_FIO || '—' }}</span>
          </div>

          <div v-if="isAdmin && item.user_phone" class="request-row">
            <strong>Телефон:</strong>
            <span class="request-text-ellipsis">{{ item.user_phone }}</span>
          </div>

          <div class="request-row">
            <strong>Ткань:</strong>
            <span class="request-text-ellipsis">{{ item.fabric_name }}</span>
          </div>

          <div class="request-row">
            <strong>Количество:</strong>
            <span>{{ item.quantity }}</span>
          </div>

          <div class="request-row">
            <strong>Срок:</strong>
            <span>{{ formatDate(item.deadline_date) }}</span>
          </div>

          <div v-if="item.headboard_pattern" class="request-row">
            <strong>Рисунок:</strong>
            <span class="request-text-ellipsis">{{ item.headboard_pattern }}</span>
          </div>

          <div v-if="item.comment" class="request-row">
            <strong>Комментарий:</strong>
            <span class="request-text-ellipsis">{{ item.comment }}</span>
          </div>

          <div v-if="item.files && item.files.length" class="images" @click.stop>
            <img
              v-for="file in item.files"
              :key="file.id"
              :src="file.file_url"
              alt=""
              class="image-item"
              @click="openImage(file.file_url)"
            />
          </div>
        </div>
      </div>

      <div v-else-if="!listLoading" class="empty-box">
        Пока заявок нет
      </div>

      <div v-if="listLoading" class="loading-box">
        Загрузка...
      </div>

      <div v-if="!hasMore && items.length" class="all-loaded-box">
        Все заявки загружены
      </div>
    </div>

    <div v-if="fullscreenImage" class="image-modal" @click="closeImage">
      <button type="button" class="image-modal__close" @click.stop="closeImage">×</button>
      <img :src="fullscreenImage" alt="" class="image-modal__content" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'RequestsView',
  data() {
    return {
      touchStartY: 0,
      pullDistance: 0,
      isPulling: false,
      isRefreshing: false,
      fullscreenImage: '',
      autoFillInProgress: false
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    },
    items() {
      return this.$store.getters['orders/items']
    },
    listLoading() {
      return this.$store.getters['orders/listLoading']
    },
    hasMore() {
      return this.$store.getters['orders/hasMore']
    },
    total() {
      return this.$store.getters['orders/total']
    },
    errorMessage() {
      return this.$store.getters['orders/error']
    },
    didInitialLoad() {
      return this.$store.getters['orders/didInitialLoad']
    }
  },
  async mounted() {
    await this.$store.dispatch('orders/loadOrders', { reset: !this.didInitialLoad })

    this.$nextTick(() => {
      this.ensureScrollableContent()
    })

    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize, { passive: true })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
  	statusClass(status) {
		  switch (status) {
		    case 'Создана':
		      return 'status--created'
		    case 'Принято':
		      return 'status--accepted'
		    case 'В процессе':
		      return 'status--progress'
		    case 'Отклонено':
		      return 'status--rejected'
		    case 'Завершено':
		      return 'status--completed'
		    default:
		      return 'status--default'
		  }
		},
    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString()
    },

    formatOrderNumber(value) {
      if (!value) return '—'

      const match = String(value).match(/(\d+)(?!.*\d)/)

      if (!match) {
        return String(value)
      }

      return String(Number(match[1]))
    },

    getDistanceToBottom() {
      const doc = document.documentElement
      return doc.scrollHeight - (window.innerHeight + window.scrollY)
    },

    async loadMoreIfNeeded() {
      if (this.listLoading || !this.hasMore) {
        return
      }

      if (this.getDistanceToBottom() <= 250) {
        const result = await this.$store.dispatch('orders/loadOrders')

        if (result?.ok) {
          this.$nextTick(() => {
            this.ensureScrollableContent()
          })
        }
      }
    },

    async ensureScrollableContent() {
      if (this.autoFillInProgress) {
        return
      }

      this.autoFillInProgress = true

      try {
        let guard = 0

        while (
          guard < 8 &&
          !this.listLoading &&
          this.hasMore &&
          document.documentElement.scrollHeight <= window.innerHeight + 120
        ) {
          const result = await this.$store.dispatch('orders/loadOrders')

          if (!result?.ok || result.loadedCount === 0) {
            break
          }

          guard += 1
        }
      } finally {
        this.autoFillInProgress = false
      }
    },

    async handleScroll() {
      await this.loadMoreIfNeeded()
    },

    async handleResize() {
      await this.ensureScrollableContent()
    },

    openOrder(orderNumber) {
      this.$router.push(`/requests/${orderNumber}`)
    },

    openImage(url) {
      this.fullscreenImage = url
    },

    closeImage() {
      this.fullscreenImage = ''
    },

    onTouchStart(event) {
      if (window.scrollY > 0 || this.listLoading) {
        return
      }

      this.touchStartY = event.touches[0].clientY
      this.isPulling = true
    },

    onTouchMove(event) {
      if (!this.isPulling) {
        return
      }

      const currentY = event.touches[0].clientY
      const diff = currentY - this.touchStartY

      if (diff > 0) {
        this.pullDistance = Math.min(diff, 140)
      }
    },

    async onTouchEnd() {
      if (!this.isPulling) {
        return
      }

      if (this.pullDistance > 90 && !this.isRefreshing) {
        this.isRefreshing = true
        await this.$store.dispatch('orders/refreshOrders')
        this.isRefreshing = false

        this.$nextTick(() => {
          this.ensureScrollableContent()
        })
      }

      this.pullDistance = 0
      this.isPulling = false
    }
  }
}
</script>

<style scoped>
.page {
  padding: 12px;
}

.card {
  max-width: 760px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 22px;
  padding: 22px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
}

.page-head h1 {
  margin: 0 0 8px;
  font-size: 30px;
}

.page-head p {
  margin: 0 0 22px;
  color: #6b7280;
}

.pull-refresh {
  margin-bottom: 16px;
}

.pull-refresh__inner {
  text-align: center;
  color: #64748b;
  font-weight: 700;
  padding: 10px 12px;
  border-radius: 14px;
  background: #f8fafc;
}

.requests-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.request-item {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px;
  cursor: pointer;
}

.request-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.request-top > div {
  min-width: 0;
  flex: 1;
}

.order-number {
  color: #2563eb;
  font-weight: 800;
  margin-bottom: 6px;
}

.request-title {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status {
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}

.status--created {
  background: #ecfdf5;
  color: #374151;
  border-color: #9fd8c8;
}

.status--accepted {
  background: #dbeafe;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.status--progress {
  background: #fef3c7;
  color: #b45309;
  border-color: #fde68a;
}

.status--rejected {
  background: #fee2e2;
  color: #b91c1c;
  border-color: #fecaca;
}

.status--completed {
  background: #dcfce7;
  color: #15803d;
  border-color: #bbf7d0;
}

.status--default {
  background: #f3f4f6;
  color: #4b5563;
  border-color: #e5e7eb;
}

.request-row {
  color: #4b5563;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.request-row strong {
  flex-shrink: 0;
}

.request-row span {
  min-width: 0;
}

.request-text-ellipsis {
  display: block;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.images {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  margin-top: 14px;
}

.image-item {
  width: 92px;
  height: 92px;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
  cursor: pointer;
}

.empty-box,
.loading-box,
.all-loaded-box {
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 28px;
  text-align: center;
  color: #64748b;
}

.all-loaded-box {
  margin-top: 18px;
}

.message {
  margin: 0 0 16px;
  padding: 14px 16px;
  border-radius: 14px;
}

.message.error {
  background: #f7dede;
  color: #c52f2f;
}

.image-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 500;
  padding: 24px;
}

.image-modal__content {
  max-width: 100%;
  max-height: 100%;
  border-radius: 18px;
}

.image-modal__close {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 46px;
  height: 46px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  font-size: 28px;
  cursor: pointer;
}
</style>